import type { ChatTurn, FutureXClient, UsageReport } from '@fim/futurex-protocol';
import { createDoneFilter } from './marker.js';
import { accumulateUsage } from './pricing.js';
import { errColor, eout, formatCount, out } from './ui.js';

/** Outcome of consuming one response stream. */
export interface RenderResult {
  /** The visible answer text (marker stripped — safe for history + display). */
  text: string;
  usage?: UsageReport;
  finishReason?: string;
  /** The completion marker appeared in this stream (the model declared done). */
  sawMarker?: boolean;
  /** The trivial-change marker appeared (the model asked to skip verification). */
  sawNoVerify?: boolean;
  /** Set if the stream ended in an error. */
  error?: string;
}

/**
 * Consume a {@link FutureXClient} stream and render it: the dim "thinking" lane
 * goes to STDERR (so `... > file` captures only the answer), the answer goes to
 * STDOUT. The completion marker ([[FX:DONE]]) is stripped from both the display
 * and the collected text — `sawMarker` reports it so the completion policy can
 * tell "finished" from "stopped". The one render path shared by `ask` and the
 * REPL.
 */
export async function renderStream(
  client: FutureXClient,
  turn: ChatTurn,
  opts: { showReasoning?: boolean } = {},
): Promise<RenderResult> {
  const showReasoning = opts.showReasoning ?? true;
  let text = '';
  let usage: UsageReport | undefined;
  let finishReason: string | undefined;
  let inThought = false;
  let wroteAnswer = false;
  const filter = createDoneFilter();

  const closeLanes = (): void => {
    const rest = filter.flush();
    if (rest) {
      out(rest);
      text += rest;
      wroteAnswer = true;
    }
    if (inThought) {
      eout('\n');
      inThought = false;
    }
    if (wroteAnswer) out('\n');
  };

  for await (const ev of client.chat(turn)) {
    switch (ev.t) {
      case 'thought':
        if (showReasoning) {
          if (!inThought) {
            eout(errColor.dim('thinking · '));
            inThought = true;
          }
          eout(errColor.dim(ev.text));
        }
        break;
      case 'token': {
        if (inThought) {
          eout('\n');
          inThought = false;
        }
        const visible = filter.push(ev.text);
        if (visible) {
          out(visible);
          text += visible;
          wroteAnswer = true;
        }
        break;
      }
      case 'usage':
        // The gateway loop emits one usage frame per model call (one per tool
        // round) — accumulate so budgets/billing see the whole turn.
        usage = usage ? accumulateUsage(usage, ev.usage) : ev.usage;
        break;
      case 'done':
        finishReason = ev.finishReason;
        break;
      case 'error':
        closeLanes();
        return { text, usage, sawMarker: filter.sawMarker, sawNoVerify: filter.sawNoVerify, error: ev.message };
      case 'status':
        break;
    }
  }

  closeLanes();
  return { text, usage, finishReason, sawMarker: filter.sawMarker, sawNoVerify: filter.sawNoVerify };
}

/** A compact one-line usage summary for stderr (with cache-hit ratio when known). */
export function usageLine(usage: UsageReport, model: string): string {
  let line = `${model} · ${formatCount(usage.inputTokens)}→${formatCount(usage.outputTokens)} tok`;
  if (usage.reasoningTokens !== undefined && usage.reasoningTokens > 0) {
    line += ` (${formatCount(usage.reasoningTokens)} reasoning)`;
  }
  const hit = usage.cacheHitTokens;
  const miss = usage.cacheMissTokens;
  if (hit !== undefined && miss !== undefined && hit + miss > 0) {
    const pct = Math.round((hit / (hit + miss)) * 100);
    line += ` · cache ${pct}%`;
  }
  return line;
}
